import { createContext, useContext, useMemo, useState, useCallback, useRef } from 'react';
import { useGeneral } from '../../Context/GeneralContext';
import { useCanEditCapacidades } from '../../usePermisos';
import { capLineaDefaultFields, toApi, toDisplay, flattenCeldas, celdaConteoDeLinea } from '../../utils/capacidades';

const CapacidadesContext = createContext();

const CELL_KEYS = ['piezasPorPaquete', 'ritmoIdeal', 'ritmoEstandar'];

// El volcado GET /capacidadesdefault trae una entrada por celda con registro:
// la de la línea (EsDeLinea=1, celda Orden=0) y las de celda individual (EsDeLinea=0).
// El GET no expone paquetesPorTarima/cantPorPieza, así que se asumen los defaults de
// esquema (null / 1) hasta que el usuario los confirme al guardar.
function capValoresFromEntry(entry) {
    return {
        ritmoIdeal: toDisplay(entry.ritmoIdeal),
        ritmoEstandar: toDisplay(entry.ritmoEstandar),
        piezasPorPaquete: entry.piezasPorPaquete ?? 0,
        paquetesPorTarima: null,
        cantPorPieza: 1,
    };
}

// Construye las filas de celda cruzando las celdas productivas (CeldaPorLinea) con
// sus registros del volcado. Sin registro propio → hereda la capacidad de línea.
// `celdaConteo` = celda fuente (la que define el conteo/capacidad de la línea).
function buildCeldas(rawCeldas, lineId, capLineaValores, capDefaults, celdaConteo) {
    return rawCeldas.map((c) => {
        const entry = capDefaults.find(
            (d) => Number(d.linea) === Number(lineId) && Number(d.celda) === Number(c.id)
        );
        const valores = entry ? capValoresFromEntry(entry) : { ...capLineaValores };
        // "Personalizada" solo si tiene registro propio con algún valor distinto al de la línea.
        const configurada = !!entry
            && CELL_KEYS.some((k) => Number(valores[k]) !== Number(capLineaValores[k]));
        return {
            celdaId: c.id,
            nombre: c.nombre,
            configurada,
            valores,
            original: { ...valores },
            saving: false,
            esFuente: celdaConteo != null && Number(c.id) === Number(celdaConteo),
        };
    });
}

async function parseErrorMessage(res) {
    try {
        const data = await res.clone().json();
        if (typeof data === 'string' && data) return data;
        if (data && typeof data === 'object' && data.message) return data.message;
    } catch { /* cuerpo no es JSON */ }
    try {
        const text = await res.text();
        return text || null;
    } catch {
        return null;
    }
}

export function CapacidadesProvider(props) {
    const { tokenR } = useGeneral();
    const canEdit = useCanEditCapacidades();

    const [areas, setAreas] = useState([]);
    const [selectedArea, setSelectedArea] = useState(null);
    const [lines, setLines] = useState([]);
    // lineData[lineId] = { expanded, loaded, loading, capLinea, celdas, lineaSaving }
    // capLinea = { valores, original, existe }
    const [lineData, setLineData] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

    const controllersRef = useRef({});
    // Cache del volcado completo de GET /capacidadesdefault (contrato: no hay filtro
    // por línea en el back, hay que filtrar en el cliente). Se pide una sola vez por
    // sesión de la vista.
    const capDefaultsRef = useRef(null);
    const capDefaultsFetchRef = useRef(null);

    const notify = useCallback((msg, severity = 'success') => {
        setSnackbar({ open: true, msg, severity });
    }, []);

    const closeSnackbar = useCallback(() => {
        setSnackbar((s) => ({ ...s, open: false }));
    }, []);

    const authHeaders = useCallback(
        () => ({ authorization: `Bearer ${tokenR.current}`, Accept: 'application/json' }),
        [tokenR]
    );

    const getAreas = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/areas`, {
                method: 'GET',
                headers: authHeaders(),
            });
            if (res.ok) setAreas(await res.json());
        } catch { /* ignore */ }
    }, [authHeaders]);

    const selectArea = useCallback(
        async (area) => {
            setSelectedArea(area);
            setLineData({});
            const url = area
                ? `${process.env.REACT_APP_SERVICE_ENDPOINT}/lineas?area=${area.area1}`
                : `${process.env.REACT_APP_SERVICE_ENDPOINT}/lineas`;
            try {
                const res = await fetch(url, { method: 'GET', headers: authHeaders() });
                if (res.ok) setLines(await res.json());
                else setLines([]);
            } catch {
                setLines([]);
            }
        },
        [authHeaders]
    );

    // GET /capacidadesdefault es un volcado completo (sin filtro por línea en el
    // back); se pide una sola vez y se filtra en el cliente por `linea`.
    const fetchCapDefaults = useCallback(
        async () => {
            if (capDefaultsRef.current) return capDefaultsRef.current;
            if (!capDefaultsFetchRef.current) {
                capDefaultsFetchRef.current = fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/capacidadesdefault`,
                    { method: 'GET', headers: authHeaders() }
                )
                    .then((res) => (res.ok ? res.json() : []))
                    .catch(() => [])
                    .then((data) => {
                        capDefaultsRef.current = Array.isArray(data) ? data : [];
                        return capDefaultsRef.current;
                    });
            }
            return capDefaultsFetchRef.current;
        },
        [authHeaders]
    );

    const loadLinea = useCallback(
        async (lineId) => {
            controllersRef.current[lineId]?.abort();
            const controller = new AbortController();
            controllersRef.current[lineId] = controller;

            setLineData((prev) => ({
                ...prev,
                [lineId]: { ...prev[lineId], loading: true },
            }));

            try {
                const [capDefaults, celdasRes] = await Promise.all([
                    fetchCapDefaults(),
                    fetch(
                        `${process.env.REACT_APP_SERVICE_ENDPOINT}/CeldaPorLinea/CeldaPorLinea?linea=${lineId}`,
                        { method: 'GET', headers: authHeaders(), signal: controller.signal }
                    ),
                ]);

                const celdasJson = celdasRes.ok ? await celdasRes.json() : [];
                const rawCeldas = flattenCeldas(celdasJson);
                const celdaConteo = celdaConteoDeLinea(celdasJson);
                const celdaIdsProductivas = new Set(rawCeldas.map((c) => Number(c.id)));

                // De las entradas de esta línea, la "capacidad de línea" es la marcada
                // EsDeLinea=1; si el GET no lo expone, es la que NO corresponde a una
                // celda productiva (la celda plantilla Orden=0).
                const entriesLinea = capDefaults.filter((d) => Number(d.linea) === Number(lineId));
                const lineaEntry =
                    entriesLinea.find((d) => d.esDeLinea === true)
                    || entriesLinea.find((d) => !celdaIdsProductivas.has(Number(d.celda)))
                    || null;
                const existe = !!lineaEntry;
                const capValores = existe
                    ? capValoresFromEntry(lineaEntry)
                    : { ritmoIdeal: 0, ritmoEstandar: 0, piezasPorPaquete: 0 };

                const celdas = buildCeldas(rawCeldas, lineId, capValores, capDefaults, celdaConteo);

                setLineData((prev) => ({
                    ...prev,
                    [lineId]: {
                        ...prev[lineId],
                        loading: false,
                        loaded: true,
                        capLinea: {
                            celda: lineaEntry?.celda ?? null,
                            valores: capValores,
                            original: { ...capValores },
                            existe,
                            fields: capLineaDefaultFields(existe),
                        },
                        celdas,
                        lineaSaving: false,
                    },
                }));
            } catch (err) {
                if (err.name === 'AbortError') return;
                setLineData((prev) => ({
                    ...prev,
                    [lineId]: { ...prev[lineId], loading: false, loaded: true, capLinea: null, celdas: [] },
                }));
                notify('No se pudieron cargar las capacidades de la línea', 'error');
            }
        },
        [authHeaders, fetchCapDefaults, notify]
    );

    const toggleLinea = useCallback(
        (lineId) => {
            setLineData((prev) => {
                const current = prev[lineId] || {};
                const nextExpanded = !current.expanded;
                if (nextExpanded && !current.loaded && !current.loading) {
                    Promise.resolve().then(() => loadLinea(lineId));
                }
                return { ...prev, [lineId]: { ...current, expanded: nextExpanded } };
            });
        },
        [loadLinea]
    );

    // ── Capacidad de línea (REAL) ──
    const setCapLineaField = useCallback((lineId, key, value) => {
        setLineData((prev) => {
            const ld = prev[lineId];
            if (!ld?.capLinea) return prev;
            return {
                ...prev,
                [lineId]: {
                    ...ld,
                    capLinea: { ...ld.capLinea, valores: { ...ld.capLinea.valores, [key]: value } },
                },
            };
        });
    }, []);

    const guardarCapLinea = useCallback(
        async (lineId) => {
            const ld = lineData[lineId];
            // Solo se editan capacidades existentes; la creación (POST /Insertar) no aplica.
            if (!ld?.capLinea || !ld.capLinea.existe) return;
            const v = ld.capLinea.valores;

            // PUT /Actualizar se identifica por celda y no admite valorRollover.
            const body = {
                celda: ld.capLinea.celda,
                piezasPorPaquete: v.piezasPorPaquete,
                ritmoIdeal: toApi(v.ritmoIdeal),
                ritmoEstandar: toApi(v.ritmoEstandar),
                paquetesPorTarima: v.paquetesPorTarima == null || v.paquetesPorTarima === '' ? null : v.paquetesPorTarima,
                cantPorPieza: v.cantPorPieza || 1,
            };

            setLineData((prev) => ({ ...prev, [lineId]: { ...prev[lineId], lineaSaving: true } }));
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/capacidadesdefault/Actualizar`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(body),
                        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                    }
                );

                if (!res.ok) {
                    if (res.status === 403) throw new Error('No tienes permisos para modificar esta capacidad');
                    if (res.status === 404) throw new Error('No existe una capacidad para esta línea. Refresca la página.');
                    throw new Error((await parseErrorMessage(res)) || 'No se pudo guardar la capacidad de línea');
                }

                setLineData((prev) => {
                    const cur = prev[lineId];
                    const newValores = {
                        piezasPorPaquete: v.piezasPorPaquete,
                        ritmoIdeal: v.ritmoIdeal,
                        ritmoEstandar: v.ritmoEstandar,
                        paquetesPorTarima: v.paquetesPorTarima ?? null,
                        cantPorPieza: v.cantPorPieza || 1,
                    };
                    // Recalcular herencia: las celdas en Default adoptan el nuevo valor de línea.
                    const celdas = cur.celdas.map((c) =>
                        c.configurada ? c : { ...c, valores: { ...newValores }, original: { ...newValores } }
                    );
                    return {
                        ...prev,
                        [lineId]: {
                            ...cur,
                            lineaSaving: false,
                            capLinea: { ...cur.capLinea, valores: newValores, original: { ...newValores } },
                            celdas,
                        },
                    };
                });
                notify('Capacidad de línea guardada');
            } catch (err) {
                setLineData((prev) => ({ ...prev, [lineId]: { ...prev[lineId], lineaSaving: false } }));
                notify(err.message || 'No se pudo guardar la capacidad de línea', 'error');
            }
        },
        [lineData, authHeaders, notify]
    );

    // ── Capacidad por celda (MOCK) ──
    const setCeldaField = useCallback((lineId, celdaId, key, value) => {
        setLineData((prev) => {
            const ld = prev[lineId];
            if (!ld) return prev;
            const celdas = ld.celdas.map((c) =>
                c.celdaId === celdaId ? { ...c, valores: { ...c.valores, [key]: value } } : c
            );
            return { ...prev, [lineId]: { ...ld, celdas } };
        });
    }, []);

    // PUT /Actualizar para una celda individual (mismo endpoint que la línea, se
    // identifica por su `celda` id). Devuelve true si guardó. Actualiza el cache del
    // volcado para que la fila conserve sus valores si se colapsa/expande la línea.
    const putCelda = useCallback(
        async (lineId, celdaId, valores) => {
            const body = {
                celda: celdaId,
                piezasPorPaquete: valores.piezasPorPaquete,
                ritmoIdeal: toApi(valores.ritmoIdeal),
                ritmoEstandar: toApi(valores.ritmoEstandar),
                paquetesPorTarima: valores.paquetesPorTarima == null || valores.paquetesPorTarima === '' ? null : valores.paquetesPorTarima,
                cantPorPieza: valores.cantPorPieza || 1,
            };
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/capacidadesdefault/Actualizar`,
                {
                    method: 'PUT',
                    body: JSON.stringify(body),
                    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                }
            );
            if (!res.ok) {
                if (res.status === 403) throw new Error('No tienes permisos para modificar esta capacidad');
                if (res.status === 404) throw new Error('Esta celda no tiene una capacidad registrada en el back');
                throw new Error((await parseErrorMessage(res)) || 'No se pudo guardar la capacidad de celda');
            }
            // Reflejar el cambio en el volcado cacheado.
            const cache = capDefaultsRef.current;
            if (Array.isArray(cache)) {
                const idx = cache.findIndex((d) => Number(d.celda) === Number(celdaId));
                const merged = {
                    linea: lineId,
                    celda: celdaId,
                    piezasPorPaquete: body.piezasPorPaquete,
                    ritmoIdeal: body.ritmoIdeal,
                    ritmoEstandar: body.ritmoEstandar,
                    esDeLinea: false,
                };
                if (idx >= 0) cache[idx] = { ...cache[idx], ...merged };
                else cache.push(merged);
            }
        },
        [authHeaders]
    );

    const guardarCapCelda = useCallback(
        async (lineId, celdaId) => {
            const ld = lineData[lineId];
            const celda = ld?.celdas.find((c) => c.celdaId === celdaId);
            if (!ld || !celda) return;
            const valores = celda.valores;

            setLineData((prev) => {
                const cur = prev[lineId];
                const celdas = cur.celdas.map((c) => (c.celdaId === celdaId ? { ...c, saving: true } : c));
                return { ...prev, [lineId]: { ...cur, celdas } };
            });
            try {
                await putCelda(lineId, celdaId, valores);
                setLineData((prev) => {
                    const cur = prev[lineId];
                    const baseLinea = cur.capLinea?.valores || {};
                    const configurada = CELL_KEYS.some((k) => Number(valores[k]) !== Number(baseLinea[k]));
                    const celdas = cur.celdas.map((c) =>
                        c.celdaId === celdaId
                            ? { ...c, configurada, original: { ...c.valores }, saving: false }
                            : c
                    );
                    return { ...prev, [lineId]: { ...cur, celdas } };
                });
                notify('Capacidad de celda guardada');
            } catch (err) {
                setLineData((prev) => {
                    const cur = prev[lineId];
                    const celdas = cur.celdas.map((c) => (c.celdaId === celdaId ? { ...c, saving: false } : c));
                    return { ...prev, [lineId]: { ...cur, celdas } };
                });
                notify(err.message || 'No se pudo guardar la capacidad de celda', 'error');
            }
        },
        [lineData, putCelda, notify]
    );

    // Restaurar al default de línea = persistir en la celda los valores de la línea.
    const resetCapCelda = useCallback(
        async (lineId, celdaId) => {
            const ld = lineData[lineId];
            if (!ld?.capLinea) return;
            const defaults = {};
            CELL_KEYS.forEach((k) => { defaults[k] = ld.capLinea.valores[k]; });
            const valores = { ...defaults, paquetesPorTarima: null, cantPorPieza: 1 };

            setLineData((prev) => {
                const cur = prev[lineId];
                const celdas = cur.celdas.map((c) => (c.celdaId === celdaId ? { ...c, saving: true } : c));
                return { ...prev, [lineId]: { ...cur, celdas } };
            });
            try {
                await putCelda(lineId, celdaId, valores);
                setLineData((prev) => {
                    const cur = prev[lineId];
                    const celdas = cur.celdas.map((c) =>
                        c.celdaId === celdaId
                            ? { ...c, configurada: false, valores: { ...defaults }, original: { ...defaults }, saving: false }
                            : c
                    );
                    return { ...prev, [lineId]: { ...cur, celdas } };
                });
                notify('Celda restaurada al default de línea', 'info');
            } catch (err) {
                setLineData((prev) => {
                    const cur = prev[lineId];
                    const celdas = cur.celdas.map((c) => (c.celdaId === celdaId ? { ...c, saving: false } : c));
                    return { ...prev, [lineId]: { ...cur, celdas } };
                });
                notify(err.message || 'No se pudo restaurar la celda', 'error');
            }
        },
        [lineData, putCelda, notify]
    );

    const values = useMemo(
        () => ({
            areas,
            selectedArea,
            lines,
            lineData,
            snackbar,
            canEdit,
            getAreas,
            selectArea,
            toggleLinea,
            setCapLineaField,
            guardarCapLinea,
            setCeldaField,
            guardarCapCelda,
            resetCapCelda,
            closeSnackbar,
        }),
        [
            areas, selectedArea, lines, lineData, snackbar, canEdit,
            getAreas, selectArea, toggleLinea, setCapLineaField, guardarCapLinea,
            setCeldaField, guardarCapCelda, resetCapCelda, closeSnackbar,
        ]
    );

    return <CapacidadesContext.Provider value={values} {...props} />;
}

export function useCapacidades() {
    const context = useContext(CapacidadesContext);
    if (!context) throw new Error('useCapacidades debe estar dentro de CapacidadesProvider');
    return context;
}
